import { faAngleDown, faAngleUp } from "@fortawesome/pro-light-svg-icons";
import {
    faPenToSquare,
    faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import {
    Box,
    Button,
    Card,
    Divider,
    NotificationInline,
    Stack,
    styled,
    useTheme,
} from "@vapor/react-material";
import dayjs from "dayjs";
import type { ReactElement, ReactNode } from "react";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
    IntermediaryType,
    type TaxSubmissionDetailDTO,
} from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { useUpdateTaxSubmissionAgencyData } from "../../../../../core/api/use-update-tax-submission-agency-data/useUpdateTaxSubmissionAgencyData";
import { DelegatedUpdater } from "../../../../components/delegated-updater/DelegatedUpdater";
import { TaxSubmissionStatusChip } from "../../../../components/tax-submission-status-chip/TaxSubmissionStatusChip";
import { TaxSubmissionAction } from "./tax-submission-action/TaxSubmissionAction";
import { UpdateAgencyDataModal } from "./update-agency-data-modal/UpdateAgencyDataModal";
import { TaxSubmissionStatus } from "../../../../../core/dtos/TaxSubmissionStatus";

export const DetailRow: React.FC<{
    divider?: boolean;
    icon?: ReactNode;
    label: string;
    value: ReactNode | string;
}> = ({ divider = true, label, icon, value }): ReactElement => {
    const theme = useTheme();
    return (
        <>
            <Stack justifyContent="space-between" direction="row" padding={0.5}>
                <Box display="flex" gap={1}>
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        variant="body500"
                    >
                        {label}
                    </Typography>
                    {icon && icon}
                </Box>
                <Box>
                    {typeof value === "string" ? (
                        <Typography variant="body">{value}</Typography>
                    ) : (
                        value
                    )}
                </Box>
            </Stack>
            {divider && <Divider light />}
        </>
    );
};
const DetailCard = styled(Card)(({ theme }) => ({
    alignItems: "center",
    backgroundColor: `${theme.palette.primary.background} !important`,
    border: "solid 1px",
    borderColor: theme.palette.primary.gainsboro,
    textAlign: "center",
}));

interface TaxSubmissionDetailSummaryProps {
    taxSubmissionDetail: TaxSubmissionDetailDTO;
}
export const TaxSubmissionDetailSummary: React.FC<
    TaxSubmissionDetailSummaryProps
> = ({ taxSubmissionDetail }): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const {
        isPending: loadingUpdateAgencyData,
        mutateAsync: updateAgencyData,
    } = useUpdateTaxSubmissionAgencyData();

    const [actionsOpen, setActionsOpen] = useState(false);
    const [updateAgencyDataOpen, setUpdateAgencyDataOpen] = useState(false);

    const handleToggleActions = useCallback(() => {
        setActionsOpen((prevState) => !prevState);
    }, []);

    const handleUpdateAgencyDataModalOpen = () => {
        setUpdateAgencyDataOpen(true);
    };

    const handleUpdateAgencyDataModalClose = () => {
        setUpdateAgencyDataOpen(false);
    };

    const handleOnUpdateAgencyDataClick = async (
        taxSubmissionId: string,
        agencyId: string,
        sendDate: Date,
    ) => {
        await updateAgencyData({ taxSubmissionId, agencyId, sendDate });
    };

    const delegatedMissingWarning =
        taxSubmissionDetail.isDelegatedEditable &&
        taxSubmissionDetail.intermediary.type === IntermediaryType.COMPANY &&
        taxSubmissionDetail.delegatedTaxId === null;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
                <Stack spacing={2}>
                    <DetailCard
                        elevation={0}
                        sx={{ height: "100%", padding: 4 }}
                    >
                        <Stack>
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                variant="body500"
                            >
                                {t(
                                    "views.home.components.tax_submission_detail_summary.tax_submission_status",
                                )}
                            </Typography>
                            <Box>
                                <TaxSubmissionStatusChip
                                    status={taxSubmissionDetail.status}
                                />
                            </Box>
                        </Stack>
                    </DetailCard>
                    <DetailCard
                        elevation={0}
                        sx={{ height: "100%", padding: 4 }}
                    >
                        <Stack>
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                variant="bodyLarge700"
                            >
                                {taxSubmissionDetail.fulfilmentsErrors}
                            </Typography>
                            <Typography
                                color={theme.palette.primary.textTitleColor}
                                variant="body500"
                            >
                                {t(
                                    "views.home.components.tax_submission_detail_summary.warnings",
                                )}
                            </Typography>
                        </Stack>
                    </DetailCard>
                </Stack>
                <Stack flexGrow={4}>
                    <DetailCard elevation={0} sx={{ padding: 2 }}>
                        <DetailRow
                            label={t(
                                "views.home.components.tax_submission_detail_summary.upload_date_label",
                            )}
                            value={dayjs(
                                taxSubmissionDetail.creationDate,
                            ).format("DD/MM/YYYY - HH:mm")}
                        />
                        <DetailRow
                            label={t(
                                "views.home.components.tax_submission_detail_summary.send_date_label",
                            )}
                            value={
                                taxSubmissionDetail.governmentAgency.sendDate &&
                                dayjs(
                                    taxSubmissionDetail.governmentAgency
                                        .sendDate,
                                ).format("DD/MM/YYYY - HH:mm")
                            }
                        />
                        <DetailRow
                            label={t(
                                "views.home.components.tax_submission_detail_summary.government_agency.id_label",
                            )}
                            value={
                                <Stack
                                    flexDirection="row"
                                    gap={2}
                                    alignItems="flex-start"
                                >
                                    {taxSubmissionDetail.governmentAgency.id
                                        ? `${taxSubmissionDetail.governmentAgency.id}`
                                        : t(
                                              "views.home.components.tax_submission_detail_summary.government_agency.id.no_data",
                                          )}
                                    {taxSubmissionDetail.isAgencyDataEditable && (
                                        <>
                                            <UpdateAgencyDataModal
                                                taxSubmissionId={
                                                    taxSubmissionDetail.id
                                                }
                                                open={updateAgencyDataOpen}
                                                onClose={
                                                    handleUpdateAgencyDataModalClose
                                                }
                                                loading={
                                                    loadingUpdateAgencyData
                                                }
                                                onEdit={
                                                    handleOnUpdateAgencyDataClick
                                                }
                                            />
                                            <FontAwesomeIcon
                                                icon={faPenToSquare}
                                                size="lg"
                                                color={
                                                    theme.palette.primary
                                                        .interactiveDefault
                                                }
                                                style={{ cursor: "pointer" }}
                                                onClick={
                                                    handleUpdateAgencyDataModalOpen
                                                }
                                            />
                                        </>
                                    )}
                                </Stack>
                            }
                        />
                        <DetailRow
                            label={t(
                                "views.home.components.tax_submission_detail_summary.operator_name_label",
                            )}
                            value={
                                taxSubmissionDetail.operatorFamilyName ||
                                taxSubmissionDetail.operatorGivenName
                                    ? `${taxSubmissionDetail.operatorGivenName} ${taxSubmissionDetail.operatorFamilyName}`
                                    : t(
                                          "views.home.components.tax_submission_detail_summary.operator_name.no_data",
                                      )
                            }
                        />
                        <DetailRow
                            label={t(
                                "views.home.components.tax_submission_detail_summary.intermediary_tax_id",
                            )}
                            value={taxSubmissionDetail.intermediary.taxId}
                        />
                        <DetailRow
                            label={t(
                                "views.home.components.tax_submission_detail_summary.intermediary_site_code",
                            )}
                            value={taxSubmissionDetail.intermediary.siteCode}
                        />
                        {taxSubmissionDetail.intermediary && (
                            <DetailRow
                                divider={false}
                                icon={
                                    delegatedMissingWarning && (
                                        <FontAwesomeIcon
                                            icon={faTriangleExclamation}
                                            color={
                                                theme.palette.primary
                                                    .baseWarningColor
                                            }
                                        />
                                    )
                                }
                                label={t(
                                    "views.home.components.tax_submission_detail_summary.delegated",
                                )}
                                value={
                                    taxSubmissionDetail.isDelegatedEditable ? (
                                        <DelegatedUpdater
                                            disabled={
                                                taxSubmissionDetail.status !==
                                                TaxSubmissionStatus.READY_TO_SEND
                                            }
                                            taxSubmissionId={
                                                taxSubmissionDetail.id
                                            }
                                            intermediary={
                                                taxSubmissionDetail.intermediary
                                            }
                                            delegatedTaxId={
                                                taxSubmissionDetail.delegatedTaxId
                                            }
                                        />
                                    ) : (
                                        taxSubmissionDetail.delegatedTaxId ||
                                        t(
                                            "views.home.components.tax_submission_detail_summary.delegated.no_data",
                                        )
                                    )
                                }
                            />
                        )}
                    </DetailCard>
                </Stack>
            </Stack>
            {delegatedMissingWarning && (
                <NotificationInline
                    icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                    severity="warning"
                    variant="outlined"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="body">
                        <Trans
                            i18nKey="views.home.components.tax_submission_detail_summary.delegated.warning"
                            components={{
                                bold: (
                                    <Typography
                                        variant="body"
                                        component="span"
                                        fontWeight={500}
                                    />
                                ),
                            }}
                        />
                    </Typography>
                </NotificationInline>
            )}
            {taxSubmissionDetail.actions &&
                taxSubmissionDetail.actions.length > 0 && (
                    <Stack gap={2}>
                        <Stack direction="row">
                            <Button
                                startIcon={
                                    <VaporIcon
                                        color="info"
                                        icon={
                                            actionsOpen
                                                ? faAngleUp
                                                : faAngleDown
                                        }
                                    />
                                }
                                onClick={handleToggleActions}
                            >
                                <Typography
                                    color={theme.palette.primary.textTitleColor}
                                    variant="bodyLarge"
                                >
                                    {t(
                                        "views.home.components.tax_submission_detail_summary.actions_accordion",
                                    )}
                                </Typography>
                            </Button>
                        </Stack>
                        {actionsOpen && (
                            <Stack gap={1} sx={{ px: 6 }}>
                                <Typography variant="bodySmall">
                                    {t(
                                        "views.home.components.tax_submission_detail_summary.subtitle",
                                    )}
                                </Typography>
                                {taxSubmissionDetail.actions.map(
                                    (action, index) => (
                                        <TaxSubmissionAction
                                            key={index}
                                            action={action}
                                        />
                                    ),
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}
        </Stack>
    );
};
